import React, { useCallback, useMemo, useState } from 'react'
import { CheckCircleFilled } from '@ant-design/icons'
import { Button, Checkbox, Divider, Flex, Modal, Typography } from 'antd'
import { useIntl } from 'react-intl'
import { useDispatch } from 'react-redux'

import useNavigation from '@/utils/hooks/useNavigation'
import { dismissPaymentConnectKledoInfo } from '@/redux/settings/actions'
import KledoPaymentConnectInfo from '@/containers/PaymentConnect/KledoPaymentConnectInfo'

import styles from './paymentGatewayOnboardingModal.module.scss'

const { Paragraph, Text } = Typography

const KLEDO_PAYMENT_CONNECT_SETTINGS_PATH = '/settings/payment-connect/4'

const PaymentGatewayActivationSuccessModal = ({
  open,
  onClose,
  variant = 'banner',
  availableMethods,
}) => {
  const { formatMessage } = useIntl()
  const { onNavigate } = useNavigation()
  const dispatch = useDispatch()
  const [dontShowAgain, setDontShowAgain] = useState(false)

  const isDetailVariant = variant === 'detail'

  const handleClose = useCallback(() => {
    if (isDetailVariant && dontShowAgain) {
      dispatch(dismissPaymentConnectKledoInfo())
    }
    onClose?.()
  }, [dispatch, dontShowAgain, isDetailVariant, onClose])

  const handleOpenSettings = useCallback(() => {
    onNavigate(KLEDO_PAYMENT_CONNECT_SETTINGS_PATH)
    onClose?.()
  }, [onClose, onNavigate])

  const footer = useMemo(() => {
    if (isDetailVariant) {
      return (
        <Flex justify="space-between" align="center" wrap="wrap" gap={12}>
          <Checkbox
            checked={dontShowAgain}
            onChange={(e) => setDontShowAgain(e.target.checked)}
            className={styles.dismissCheckbox}
          >
            {formatMessage({ id: 'appOnboarding.paymentGateway.info.dont_show_again' })}
          </Checkbox>
          <Button type="primary" onClick={handleClose}>
            {formatMessage({ id: 'appOnboarding.paymentGateway.success_modal_btn' })}
          </Button>
        </Flex>
      )
    }
    return [
      <Button key="ok" onClick={handleClose}>
        {formatMessage({ id: 'appOnboarding.paymentGateway.success_modal_btn' })}
      </Button>,
    ]
  }, [dontShowAgain, formatMessage, handleClose, isDetailVariant])

  return (
    <Modal
      open={open}
      title={formatMessage({ id: 'appOnboarding.paymentGateway.success_modal_title' })}
      onCancel={handleClose}
      footer={footer}
      maskClosable={false}
      keyboard={false}
      destroyOnHidden
      width={720}
      styles={{
        body: { paddingTop: 12 },
      }}
    >
      <div className={styles.modalBody}>
        <Flex vertical gap={20} align="center" className={styles.heroBlock}>
          <div className={styles.successIconWrap} aria-hidden>
            <CheckCircleFilled style={{ fontSize: 48, color: '#fff' }} />
          </div>
          <Paragraph style={{ marginBottom: 0, textAlign: 'center', fontSize: 15 }}>
            {formatMessage({ id: 'appOnboarding.paymentGateway.success_modal_body' })}
          </Paragraph>
          {!isDetailVariant && (
            <>
              <Button type="primary" size="large" block onClick={handleOpenSettings}>
                {formatMessage({ id: 'appOnboarding.paymentGateway.success_modal_link' })}
              </Button>
              <Text type="secondary" style={{ fontSize: 13, textAlign: 'center' }}>
                {formatMessage({ id: 'appOnboarding.paymentGateway.success_modal_hint' })}
              </Text>
            </>
          )}
        </Flex>
        <Divider className={styles.divider} />
        <KledoPaymentConnectInfo availableMethods={availableMethods} />
      </div>
    </Modal>
  )
}

export default PaymentGatewayActivationSuccessModal
